// /test-trenera/:token — VEŘEJNÁ stránka screening testu pro kandidáta na trenéra.
//
// Kandidát není přihlášený. Stránka je mimo <RequireAuth> i mimo <Layout>
// (žádný sidebar, žádné menu) — vidí jen test. Autorizuje ho token v URL,
// který mu pošle šéftrenér z HR › Potenciální trenéři.
//
// Design: brand fialová appky, bílé karty, velké dotykové cíle. Mobil first —
// jedna otázka na obrazovku, sticky progress nahoře, tlačítka přes celou šířku.
//
// Vyhodnocení NEBĚŽÍ tady — otázky přijdou bez správných odpovědí a skóre
// spočítá backend (netlify/functions/_lib/screeningTest.cjs). Kandidát svůj
// výsledek nevidí, jen poděkování.

import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { BookOpen, Check, ChevronLeft, ChevronRight, Loader2, ShieldCheck, AlertCircle } from "lucide-react";

export default function KandidatTest() {
  const { token } = useParams();
  const [stav, setStav] = useState("load");   // load | intro | test | hotovo | jiz_hotovo | chyba
  const [chyba, setChyba] = useState("");
  const [jmeno, setJmeno] = useState("");
  const [otazky, setOtazky] = useState([]);
  const [manual, setManual] = useState(null);
  const [odpovedi, setOdpovedi] = useState([]);
  const [idx, setIdx] = useState(0);
  const [manualOpen, setManualOpen] = useState(false);
  const [odesilam, setOdesilam] = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    let zrus = false;
    (async () => {
      try {
        const r = await fetch(`/api/hr-kandidat-test?token=${encodeURIComponent(token || "")}`);
        const j = await r.json();
        if (zrus) return;
        if (!r.ok) { setChyba(j.error || "Odkaz se nepodařilo otevřít."); setStav("chyba"); return; }
        if (j.hotovo) { setStav("jiz_hotovo"); return; }
        setJmeno(j.jmeno || "");
        setOtazky(j.otazky || []);
        setManual(j.manual || null);
        setOdpovedi(new Array((j.otazky || []).length).fill(null));
        setStav("intro");
      } catch (_) {
        if (!zrus) { setChyba("Nepodařilo se připojit. Zkontroluj internet a zkus to znovu."); setStav("chyba"); }
      }
    })();
    return () => { zrus = true; };
  }, [token]);

  const krok = otazky[idx];
  const hotovoOtazek = odpovedi.filter((o) => o !== null).length;
  const progress = otazky.length ? Math.round((idx / otazky.length) * 100) : 0;
  const posledni = idx === otazky.length - 1;

  // Sekce B pracuje s manuálem — u těchto otázek nabídneme úryvky rozbalené.
  useEffect(() => { setManualOpen(!!(krok && krok.manual)); }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollNahoru = () => { try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) { window.scrollTo(0, 0); } };

  const zvol = (i) => setOdpovedi((p) => { const n = [...p]; n[idx] = i; return n; });

  const dal = async () => {
    if (odpovedi[idx] === null) return;
    if (!posledni) { setIdx((v) => v + 1); scrollNahoru(); return; }
    setOdesilam(true);
    try {
      const r = await fetch(`/api/hr-kandidat-test?token=${encodeURIComponent(token || "")}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ odpovedi }),
      });
      const j = await r.json();
      if (!r.ok) { setChyba(j.error || "Odeslání se nepovedlo."); setStav("chyba"); return; }
      setStav("hotovo");
      scrollNahoru();
    } catch (_) {
      setChyba("Odeslání se nepovedlo. Zkontroluj internet a zkus to znovu.");
      setOdesilam(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div ref={topRef} />

      {/* Hlavička — na mobilu se stackuje, na PC v řádku */}
      <div className="bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 text-white">
        <div className="max-w-2xl mx-auto px-4 py-5 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[11px] sm:text-xs uppercase tracking-wide text-white/70 mb-1">
                FitWay40, výběrové řízení
              </div>
              <div className="text-xl sm:text-2xl font-bold font-serif leading-tight">
                Vstupní test pro trenéry
              </div>
            </div>
            <div className="text-[11px] sm:text-xs text-white/70 flex items-center gap-1.5 shrink-0">
              <ShieldCheck className="w-4 h-4" /> Vyplňuje se jednou
            </div>
          </div>
        </div>
        {stav === "test" && (
          <div className="h-1.5 bg-white/15">
            <div className="h-full bg-brand-300 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8 pb-24">
        {stav === "load" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 flex items-center justify-center gap-3 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" /> Načítám test…
          </div>
        )}

        {stav === "chyba" && (
          <div className="bg-white border border-rose-200 rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-xl bg-rose-100 text-rose-600 shrink-0"><AlertCircle className="w-5 h-5" /></div>
              <div className="min-w-0">
                <div className="font-bold text-slate-800">Test se nepodařilo otevřít</div>
                <p className="text-sm text-slate-600 mt-1">{chyba}</p>
                <p className="text-sm text-slate-500 mt-3">Ozvi se prosím tomu, kdo ti odkaz poslal, pošle ti nový.</p>
              </div>
            </div>
          </div>
        )}

        {stav === "jiz_hotovo" && (
          <Potvrzeni
            titulek="Test už máš odeslaný"
            text="Odpovědi jsme uložili. Ozveme se ti s dalším krokem."
          />
        )}

        {stav === "hotovo" && (
          <Potvrzeni
            titulek={jmeno ? `Díky, ${jmeno}!` : "Díky!"}
            text="Test je odeslaný. Odpovědi si projdeme a ozveme se ti s dalším krokem."
          />
        )}

        {stav === "intro" && (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 sm:p-6">
              <div className="text-lg sm:text-xl font-bold text-slate-800">
                {jmeno ? `Ahoj ${jmeno},` : "Ahoj,"}
              </div>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                tohle je krátký vstupní test. Zabere ti zhruba 10 minut a zjišťuje tři věci: jak
                uvažuješ, jestli si umíš najít odpověď v manuálu a jak se rozhoduješ v reálných
                situacích s klientem.
              </p>
              <div className="mt-4 grid gap-2">
                <IntroBod cislo="1" nadpis="Logika a úsudek" popis="6 otázek" />
                <IntroBod cislo="2" nadpis="Práce s manuálem" popis="4 otázky, úryvky z manuálu máš vždy po ruce" />
                <IntroBod cislo="3" nadpis="Praktický úsudek" popis="4 otázky ze situací na place" />
              </div>
              <div className="mt-4 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-3 leading-relaxed">
                Odpovídej sám za sebe a v klidu. Test jde odeslat jen jednou, takže si ho projdi
                v jednom zátahu. Zpátky se během vyplňování dostaneš tlačítkem Zpět.
              </div>
              <button
                onClick={() => { setStav("test"); scrollNahoru(); }}
                className="btn btn-primary mt-5 w-full"
              >
                Začít test <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {stav === "test" && krok && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 text-xs">
              <span className="font-semibold text-brand-700 bg-brand-50 border border-brand-200 rounded-xl px-2.5 py-1">
                {krok.tag}
              </span>
              <span className="text-slate-500 tabular-nums shrink-0">
                Otázka {idx + 1} / {otazky.length}
              </span>
            </div>

            {/* Manuál — u sekce B rozbalený, jinde schovaný */}
            {manual && (
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setManualOpen((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="flex items-center gap-2 font-semibold text-slate-700 text-sm min-w-0">
                    <BookOpen className="w-4 h-4 text-brand-600 shrink-0" />
                    <span className="truncate">{manual.title}</span>
                  </span>
                  <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${manualOpen ? "rotate-90" : ""}`} />
                </button>
                {manualOpen && (
                  <div className="px-4 pb-4 space-y-2.5 border-t border-slate-100 pt-3">
                    {manual.paragraphs.map((p, i) => (
                      <p key={i} className="text-[13px] text-slate-600 leading-relaxed">{p}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-6">
              <div className="text-base sm:text-lg font-bold text-slate-800 leading-snug [overflow-wrap:anywhere]">
                {krok.q}
              </div>
              <div className="mt-4 grid gap-2.5">
                {krok.opts.map((o, i) => {
                  const vybrano = odpovedi[idx] === i;
                  return (
                    <button
                      key={i}
                      onClick={() => zvol(i)}
                      className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-xl border transition-all ${
                        vybrano
                          ? "bg-brand-50 border-brand-400 ring-2 ring-brand-200"
                          : "bg-white border-slate-200 hover:border-brand-300 hover:bg-slate-50"
                      }`}
                    >
                      <span className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-xs font-bold mt-0.5 ${
                        vybrano ? "bg-brand-600 border-brand-600 text-white" : "border-slate-300 text-slate-400"
                      }`}>
                        {vybrano ? <Check className="w-3.5 h-3.5" /> : String.fromCharCode(65 + i)}
                      </span>
                      <span className={`text-sm leading-relaxed ${vybrano ? "text-slate-800 font-medium" : "text-slate-600"}`}>
                        {o}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => { setIdx((v) => Math.max(0, v - 1)); scrollNahoru(); }}
                disabled={idx === 0 || odesilam}
                className="btn btn-secondary"
              >
                <ChevronLeft className="w-4 h-4" /> Zpět
              </button>
              <button
                onClick={dal}
                disabled={odpovedi[idx] === null || odesilam}
                className="btn btn-primary flex-1"
              >
                {odesilam ? (<><Loader2 className="w-4 h-4 animate-spin" /> Odesílám…</>)
                  : posledni ? (<>Odeslat test <Check className="w-4 h-4" /></>)
                  : (<>Pokračovat <ChevronRight className="w-4 h-4" /></>)}
              </button>
            </div>

            <div className="text-center text-[11px] text-slate-400">
              Zodpovězeno {hotovoOtazek} z {otazky.length}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function IntroBod({ cislo, nadpis, popis }) {
  return (
    <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
      <span className="shrink-0 w-6 h-6 rounded-xl bg-brand-100 text-brand-700 text-xs font-bold flex items-center justify-center">{cislo}</span>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-800">{nadpis}</div>
        <div className="text-xs text-slate-500">{popis}</div>
      </div>
    </div>
  );
}

function Potvrzeni({ titulek, text }) {
  return (
    <div className="bg-white border border-emerald-200 rounded-2xl shadow-sm p-6 sm:p-8 text-center">
      <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
        <Check className="w-7 h-7" />
      </div>
      <div className="text-xl font-bold text-slate-800 mt-4">{titulek}</div>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-sm mx-auto">{text}</p>
    </div>
  );
}
